/**
 * The in-app quote PDF viewer — owner decisions 2026-10-25.
 *
 * One viewer, many hosts: the quote page, the Quotes list (rows, phone cards,
 * kanban cards), the deal page, the Deals list, the Send for signature modal and
 * the dashboards. Each page mounts ONE host through {@link useQuotePdfPreview}
 * and hands `preview.open` to every {@link PreviewQuotePdfButton} it renders —
 * never a modal per row.
 *
 * Gates and wording live in `@/lib/quote-pdf-preview` (preview = `quotes.read`,
 * Send to Xero and preview = `quotes.update`) and `@/lib/document-pdf` (the
 * mark-sent confirmation and the refusal sentences). This file owns state only.
 *
 * - `GET /quotes/:id/pdf/preview` is a pure read: it fetches Xero's PDF and
 *   says whether SEED EMERGE holds edits Xero has not received (`behind`).
 * - 409 `XERO_DOCUMENT_NOT_IN_XERO` offers "Send to Xero and preview" to a
 *   reader with `quotes.update` (`POST /quotes/:id/xero/push`); a push that
 *   comes back `skipped`/`failed` shows the push's own sentence.
 * - Download saves the blob already on screen (no refetch), `quotes.export`.
 * - Mark Sent is a write into Xero that SEED EMERGE cannot undo, so it is
 *   confirmed first, `quotes.send`; the returned PDF replaces the one shown.
 *
 * ⚠️ **Object URLs are revoked imperatively at every site that drops one**, via
 * `urlRef`, and the load effect is keyed on the quote id — never on the URL. An
 * effect keyed on the URL would, under StrictMode's double-invoke, revoke the
 * blob the second run renders (see `SendFromXeroButton`).
 *
 * ⚠️ No `sandbox` on the iframe: a same-origin blob PDF needs the browser's
 * built-in viewer, which a sandbox without `allow-scripts` disables.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { EyeIcon } from '@/components/catalog/icons';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Modal } from '@/components/ui/Modal';
import { Notice } from '@/components/ui/Notice';
import { Skeleton } from '@/components/ui/Skeleton';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import {
  MARK_SENT_DIALOG_BODY,
  MARK_SENT_DIALOG_TITLE,
  pdfRefusalMessage,
  XERO_DOCUMENT_NOT_IN_XERO_CODE,
} from '@/lib/document-pdf';
import { saveBlob } from '@/lib/download';
import {
  canPreviewQuotePdf,
  canPushQuoteToXero,
  PREVIEW_PDF_LABEL,
  quotePdfBehindNotice,
  quotePdfPreviewRefusal,
  quotePushRefusal,
  quoteSignHref,
  SEND_TO_XERO_AND_PREVIEW_LABEL,
} from '@/lib/quote-pdf-preview';
import { useAuth } from '@/lib/use-auth';

/** What the viewer needs off a quote — satisfied by a list row and the full record alike. */
export interface QuotePdfPreviewTarget {
  id: number;
  /** The SEED EMERGE number, e.g. `EQ-00001`. */
  quoteNumber: string | null;
  subject?: string | null;
  companyName?: string | null;
}

export interface QuotePdfPreviewModalProps {
  /** `null` = closed. */
  target: QuotePdfPreviewTarget | null;
  onClose: () => void;
  /** After Mark Sent or Send to Xero succeeded — the host refetches. */
  onChanged?: () => void;
  /** Absent → the action navigates to `quoteSignHref(id)`. The viewer closes first either way. */
  onSendForSignature?: (target: QuotePdfPreviewTarget) => void;
  /** True on the quote page itself. */
  hideOpenQuote?: boolean;
  /** True inside SendForSignatureModal, where the action is redundant. */
  hideSendForSignature?: boolean;
  /** A non-empty string renders Send for signature disabled, with this reason on its tooltip. */
  sendForSignatureDisabledReason?: string | null;
}

interface LoadedPdf {
  url: string;
  filename: string;
  blob: Blob;
  behind: boolean;
  syncedAt: string | null;
}

/** `null` while the current request is in flight. */
type Outcome = { kind: 'ready' } | { kind: 'error'; message: string; notInXero: boolean };

const MARK_SENT_LABEL = 'Mark Sent';

/**
 * Closed = nothing mounted. The viewer is keyed on the quote id, so closing or
 * switching quotes unmounts it: every request is aborted, the object URL is
 * revoked, and no state (an open confirm, a push sentence) survives into the
 * next opening.
 */
export function QuotePdfPreviewModal(props: QuotePdfPreviewModalProps) {
  if (props.target === null) return null;
  return <QuotePdfViewer key={props.target.id} {...props} target={props.target} />;
}

function QuotePdfViewer({
  target,
  onClose,
  onChanged,
  onSendForSignature,
  hideOpenQuote = false,
  hideSendForSignature = false,
  sendForSignatureDisabledReason,
}: QuotePdfPreviewModalProps & { target: QuotePdfPreviewTarget }) {
  const { ability } = useAuth();
  const navigate = useNavigate();
  const targetId = target.id;

  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [pdf, setPdf] = useState<LoadedPdf | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [pushing, setPushing] = useState(false);
  const [pushMessage, setPushMessage] = useState<string | null>(null);

  const [confirmingMarkSent, setConfirmingMarkSent] = useState(false);
  const [markingSent, setMarkingSent] = useState(false);
  const [markSentError, setMarkSentError] = useState<string | null>(null);

  /** The live object URL; what every revoke reads. Written with the state, never after it. */
  const urlRef = useRef<string | null>(null);
  /** False once unmounted — a push (no signal) that lands after close is ignored. */
  const aliveRef = useRef(true);
  const pushingRef = useRef(false);
  const markAbortRef = useRef<AbortController | null>(null);

  const releaseUrl = useCallback(() => {
    if (urlRef.current !== null) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  const showPdf = useCallback(
    (next: Omit<LoadedPdf, 'url'>) => {
      releaseUrl();
      const url = URL.createObjectURL(next.blob);
      urlRef.current = url;
      setPdf({ ...next, url });
    },
    [releaseUrl],
  );

  /* Unmount: abort the mark-sent write's read-back and revoke the URL. */
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      markAbortRef.current?.abort();
      markAbortRef.current = null;
      releaseUrl();
    };
  }, [releaseUrl]);

  useEffect(() => {
    const controller = new AbortController();

    api.previewQuotePdf(targetId, controller.signal).then(
      (result) => {
        if (controller.signal.aborted) return;
        showPdf({
          filename: result.filename,
          blob: result.blob,
          behind: result.behind,
          syncedAt: result.syncedAt,
        });
        setPushMessage(null);
        setOutcome({ kind: 'ready' });
      },
      (caught: unknown) => {
        if (controller.signal.aborted) return;
        setOutcome({
          kind: 'error',
          message: quotePdfPreviewRefusal(caught),
          notInXero: caught instanceof ApiError && caught.code === XERO_DOCUMENT_NOT_IN_XERO_CODE,
        });
      },
    );

    return () => controller.abort();
  }, [targetId, reloadKey, showPdf]);

  const pushToXero = useCallback(async () => {
    if (pushingRef.current) return;
    pushingRef.current = true;
    setPushing(true);
    setPushMessage(null);
    try {
      const response = await api.pushQuoteToXero(targetId);
      if (!aliveRef.current) return;
      const refusal = quotePushRefusal(response.data.xeroPush);
      if (refusal === null) {
        onChanged?.();
        setOutcome(null);
        setReloadKey((key) => key + 1);
      } else {
        setPushMessage(refusal);
      }
    } catch (caught) {
      if (!aliveRef.current) return;
      setPushMessage(
        caught instanceof ApiError
          ? caught.message
          : 'The quote could not be sent to Xero. Check your connection and try again.',
      );
    } finally {
      pushingRef.current = false;
      if (aliveRef.current) setPushing(false);
    }
  }, [targetId, onChanged]);

  const markSent = useCallback(async () => {
    if (markAbortRef.current !== null) return;
    const controller = new AbortController();
    markAbortRef.current = controller;
    setMarkingSent(true);
    setMarkSentError(null);
    try {
      const result = await api.downloadQuotePdfAsSent(targetId, controller.signal);
      if (controller.signal.aborted) return;
      showPdf({
        filename: result.filename,
        blob: result.blob,
        behind: pdf?.behind ?? false,
        syncedAt: pdf?.syncedAt ?? null,
      });
      setConfirmingMarkSent(false);
      onChanged?.();
    } catch (caught) {
      if (controller.signal.aborted) return;
      setMarkSentError(pdfRefusalMessage(caught, 'quote'));
    } finally {
      if (markAbortRef.current === controller) markAbortRef.current = null;
      if (!controller.signal.aborted) setMarkingSent(false);
    }
  }, [targetId, showPdf, pdf, onChanged]);

  const phase = outcome?.kind ?? 'loading';
  const error = outcome?.kind === 'error' ? outcome.message : null;
  const notInXero = outcome?.kind === 'error' && outcome.notInXero;

  const canDownload = ability.canExport('quotes');
  const canMarkSent = ability.canSend('quotes');
  const canSign = ability.canSend('quotes');
  const signDisabledReason = sendForSignatureDisabledReason?.trim() || null;
  const canPush = canPushQuoteToXero(ability);

  const sendForSignature = () => {
    onClose();
    if (onSendForSignature) onSendForSignature(target);
    else navigate(quoteSignHref(target.id));
  };

  const named = [target.subject?.trim(), target.companyName?.trim()].filter(Boolean).join(' · ');
  const ready = phase === 'ready' && pdf !== null;
  const numberLabel = target.quoteNumber?.trim() || 'Quote';
  /*
   * Android Chrome and the Capacitor Android WebView have no built-in PDF
   * viewer, so an iframe over the blob is blank there (they report
   * `pdfViewerEnabled === false`). Those devices get an "Open PDF" link that
   * hands the blob to the system viewer instead of an empty frame.
   */
  const inlinePdfUnsupported =
    typeof navigator !== 'undefined' &&
    (navigator as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled === false;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={`${PREVIEW_PDF_LABEL} · ${numberLabel}`}
        {...(named ? { description: named } : {})}
        size="lg"
        footer={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            {!hideOpenQuote && (
              <TextLink to={`/quotes/${target.id}`} onClick={onClose} className="mr-auto text-sm">
                Open quote
              </TextLink>
            )}
            {ready && canDownload && (
              <Button variant="secondary" onClick={() => saveBlob(pdf.filename, pdf.blob)}>
                Download
              </Button>
            )}
            {ready && canMarkSent && (
              <Button
                variant="secondary"
                onClick={() => {
                  setMarkSentError(null);
                  setConfirmingMarkSent(true);
                }}
              >
                {MARK_SENT_LABEL}
              </Button>
            )}
            {canSign && !hideSendForSignature && (
              /* `tooltip` on a disabled Button renders on a wrapper (Button's own rule). */
              <Button
                variant="secondary"
                onClick={sendForSignature}
                disabled={signDisabledReason !== null}
                {...(signDisabledReason !== null ? { tooltip: signDisabledReason } : {})}
              >
                Send for signature
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        }
      >
        {phase === 'loading' && <Skeleton className="h-[60svh] w-full sm:h-[70svh]" />}

        {phase === 'error' && (
          <div className="space-y-3">
            <Notice
              tone="danger"
              role="alert"
              {...(notInXero && canPush
                ? {
                    action: (
                      <Button variant="primary" size="sm" loading={pushing} onClick={pushToXero}>
                        {SEND_TO_XERO_AND_PREVIEW_LABEL}
                      </Button>
                    ),
                  }
                : {})}
            >
              {error}
            </Notice>
            {pushMessage !== null && (
              <Notice tone="warning" role="alert">
                {pushMessage}
              </Notice>
            )}
          </div>
        )}

        {ready && (
          <div className="space-y-3">
            {pdf.behind && (
              <Notice tone="warning" role="status">
                {quotePdfBehindNotice(pdf.syncedAt)}
              </Notice>
            )}
            {inlinePdfUnsupported ? (
              <div className="flex flex-col items-center gap-3 rounded-md border border-border bg-surface-sunken px-4 py-10 text-center">
                <p className="text-sm text-fg-muted">This device cannot show a PDF inside SEED EMERGE.</p>
                <a
                  href={pdf.url}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex h-9 items-center rounded-md border border-border bg-surface px-3 text-sm font-medium text-fg hover:bg-surface-hover max-sm:h-11"
                >
                  Open PDF
                </a>
              </div>
            ) : (
              <div className="h-[60svh] min-w-0 overflow-hidden rounded-md border border-border bg-surface-sunken sm:h-[70svh]">
                <iframe
                  src={pdf.url}
                  title={`${numberLabel} as Xero renders it`}
                  className="h-full w-full border-0"
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={confirmingMarkSent}
        // Not closable mid-write, or a refusal would land on a closed dialog.
        onClose={() => {
          if (!markingSent) setConfirmingMarkSent(false);
        }}
        onConfirm={() => void markSent()}
        title={MARK_SENT_DIALOG_TITLE}
        description={MARK_SENT_DIALOG_BODY}
        confirmLabel={MARK_SENT_LABEL}
        busyLabel="Marking Sent"
        tone="primary"
        loading={markingSent}
        error={markSentError}
      />
    </>
  );
}

/**
 * One host per page:
 * `const preview = useQuotePdfPreview(opts); … preview.open(target); … {preview.element}`
 */
export function useQuotePdfPreview(
  opts?: Omit<QuotePdfPreviewModalProps, 'target' | 'onClose'>,
): { open: (target: QuotePdfPreviewTarget) => void; element: ReactNode } {
  const [target, setTarget] = useState<QuotePdfPreviewTarget | null>(null);
  const open = useCallback((next: QuotePdfPreviewTarget) => setTarget(next), []);
  const close = useCallback(() => setTarget(null), []);

  return {
    open,
    element: <QuotePdfPreviewModal {...opts} target={target} onClose={close} />,
  };
}

export interface PreviewQuotePdfButtonProps {
  target: QuotePdfPreviewTarget;
  onPreview: (target: QuotePdfPreviewTarget) => void;
  /** `detail` = labelled secondary button; `row` / `card` = icon button with a tooltip. */
  variant: 'detail' | 'row' | 'card';
}

export function PreviewQuotePdfButton({ target, onPreview, variant }: PreviewQuotePdfButtonProps) {
  const { ability } = useAuth();
  if (!canPreviewQuotePdf(ability)) return null;

  if (variant === 'detail') {
    return (
      <Button variant="secondary" leadingIcon={<EyeIcon />} onClick={() => onPreview(target)}>
        {PREVIEW_PDF_LABEL}
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      leadingIcon={<EyeIcon />}
      aria-label={`${PREVIEW_PDF_LABEL} · ${target.quoteNumber ?? 'Quote'}`}
      tooltip={PREVIEW_PDF_LABEL}
      onClick={(event) => {
        event.stopPropagation();
        onPreview(target);
      }}
    />
  );
}
